import { TranslationKey, Translator } from "../i18n/translation-key"
import { JobLabel } from "./job-label"

export enum SystemLabel {
  AKUTSERVICE = "AKUTSERVICE",
  FORSAKRING = "FORSAKRING",
  GARANTI = "GARANTI",
  LEASING = "LEASING",
  MEKANIKER_BOKNING = "MEKANIKER_BOKNING",
  KUND_BOKNING = "KUND_BOKNING",
  JULO_BOKNING = "JULO_BOKNING",
}

interface SystemLabelInfo {
  kind: SystemLabel
  id: string
  nameKey: TranslationKey
  color: string
}

export const SYSTEM_LABEL_INFO: Record<SystemLabel, SystemLabelInfo> = {
  [SystemLabel.AKUTSERVICE]: {
    kind: SystemLabel.AKUTSERVICE,
    id: "00000000-0000-4000-8000-000000000001",
    nameKey: "common.systemLabel.emergencyService",
    color: "#EA580C",
  },
  [SystemLabel.FORSAKRING]: {
    kind: SystemLabel.FORSAKRING,
    id: "00000000-0000-4000-8000-000000000002",
    nameKey: "common.systemLabel.insurance",
    color: "#0369A1",
  },
  [SystemLabel.GARANTI]: {
    kind: SystemLabel.GARANTI,
    id: "00000000-0000-4000-8000-000000000003",
    nameKey: "common.systemLabel.warranty",
    color: "#65A30D",
  },
  [SystemLabel.LEASING]: {
    kind: SystemLabel.LEASING,
    id: "00000000-0000-4000-8000-000000000004",
    nameKey: "common.systemLabel.leasing",
    color: "#DB2777",
  },
  [SystemLabel.MEKANIKER_BOKNING]: {
    kind: SystemLabel.MEKANIKER_BOKNING,
    id: "00000000-0000-4000-8000-000000000005",
    nameKey: "common.systemLabel.mechanicBooking",
    color: "#475569",
  },
  [SystemLabel.KUND_BOKNING]: {
    kind: SystemLabel.KUND_BOKNING,
    id: "00000000-0000-4000-8000-000000000006",
    nameKey: "common.systemLabel.customerBooking",
    color: "#0891B2",
  },
  [SystemLabel.JULO_BOKNING]: {
    kind: SystemLabel.JULO_BOKNING,
    id: "00000000-0000-4000-8000-000000000007",
    nameKey: "common.systemLabel.juloBooking",
    color: "#7C3AED",
  },
}

const SYSTEM_LABEL_BY_ID: Record<string, SystemLabel> = Object.fromEntries(
  Object.values(SYSTEM_LABEL_INFO).map((info) => [info.id, info.kind]),
)

export const SYSTEM_LABEL_IDS: ReadonlySet<string> = new Set(
  Object.values(SYSTEM_LABEL_INFO).map((info) => info.id),
)

const SYSTEM_LABEL_KINDS: ReadonlySet<string> = new Set(
  Object.values(SystemLabel),
)

export function isSystemLabelKind(value: string): value is SystemLabel {
  return SYSTEM_LABEL_KINDS.has(value)
}

export function isSystemLabelId(id: string): boolean {
  return SYSTEM_LABEL_IDS.has(id)
}

export function systemLabelKindFromId(id: string): SystemLabel | null {
  return SYSTEM_LABEL_BY_ID[id] ?? null
}

export function systemLabelToJobLabel(
  kind: SystemLabel,
  t: Translator,
): JobLabel {
  const info = SYSTEM_LABEL_INFO[kind]
  return {
    id: info.id,
    name: t(info.nameKey),
    color: info.color,
    isSystem: true,
    isLeasingCompany: false,
  }
}

export function leasingCompanyToJobLabel(company: {
  id: string
  name: string
}): JobLabel {
  return {
    id: company.id,
    name: company.name,
    color: SYSTEM_LABEL_INFO[SystemLabel.LEASING].color,
    isSystem: true,
    isLeasingCompany: true,
  }
}

/** A leasing job has the generic Leasing label or any leasing company label. */
export function isLeasingJobLabels(labels: JobLabel[]): boolean {
  return labels.some(
    (label) =>
      label.isLeasingCompany ||
      label.id === SYSTEM_LABEL_INFO[SystemLabel.LEASING].id,
  )
}

export function allSystemLabelsAsJobLabels(t: Translator): JobLabel[] {
  return Object.values(SystemLabel).map((kind) =>
    systemLabelToJobLabel(kind, t),
  )
}

export interface SplitLabelIds {
  customLabelIds: string[]
  systemLabelKinds: SystemLabel[]
}

export function splitLabelIds(labelIds: string[]): SplitLabelIds {
  const customLabelIds: string[] = []
  const systemLabelKinds: SystemLabel[] = []
  const seenSystem = new Set<SystemLabel>()
  for (const id of labelIds) {
    const kind = systemLabelKindFromId(id)
    if (kind) {
      if (!seenSystem.has(kind)) {
        seenSystem.add(kind)
        systemLabelKinds.push(kind)
      }
    } else {
      customLabelIds.push(id)
    }
  }
  return { customLabelIds, systemLabelKinds }
}

export enum LabelGroup {
  BOKNING = "BOKNING",
}

interface LabelGroupInfo {
  kind: LabelGroup
  id: string
  nameKey: TranslationKey
  color: string
  members: SystemLabel[]
}

export const LABEL_GROUP_INFO: Record<LabelGroup, LabelGroupInfo> = {
  [LabelGroup.BOKNING]: {
    kind: LabelGroup.BOKNING,
    id: "00000000-0000-4000-8000-0000000000A1",
    nameKey: "common.systemLabel.bookingGroup",
    color: "#1F2937",
    members: [
      SystemLabel.MEKANIKER_BOKNING,
      SystemLabel.KUND_BOKNING,
      SystemLabel.JULO_BOKNING,
    ],
  },
}

const LABEL_GROUP_BY_ID: Record<string, LabelGroup> = Object.fromEntries(
  Object.values(LABEL_GROUP_INFO).map((info) => [info.id, info.kind]),
)

export function isLabelGroupId(id: string): boolean {
  return id in LABEL_GROUP_BY_ID
}

export function labelGroupKindFromId(id: string): LabelGroup | null {
  return LABEL_GROUP_BY_ID[id] ?? null
}

export function getLabelGroupMemberIds(kind: LabelGroup): string[] {
  return LABEL_GROUP_INFO[kind].members.map(
    (member) => SYSTEM_LABEL_INFO[member].id,
  )
}

export function allLabelGroupsAsJobLabels(t: Translator): JobLabel[] {
  return Object.values(LABEL_GROUP_INFO).map((info) => ({
    id: info.id,
    name: t(info.nameKey),
    color: info.color,
    isSystem: true,
    isLeasingCompany: false,
  }))
}

export function expandLabelGroupIds(labelIds: string[]): string[] {
  const expanded: string[] = []
  const seen = new Set<string>()
  for (const id of labelIds) {
    const groupKind = labelGroupKindFromId(id)
    if (groupKind) {
      for (const memberId of getLabelGroupMemberIds(groupKind)) {
        if (!seen.has(memberId)) {
          seen.add(memberId)
          expanded.push(memberId)
        }
      }
    } else if (!seen.has(id)) {
      seen.add(id)
      expanded.push(id)
    }
  }
  return expanded
}
